'use client';

import React, { useRef } from 'react';
import { clsx } from 'clsx';
import {
  Check,
  ClipboardList,
  MoreVertical,
  Pin,
} from 'lucide-react';
import type { FavoriteItem } from '@paeffgen/react/crm-documents/data-access-graphql';
import {
  ProductImage,
  resolveLabelColor,
} from '@paeffgen/react/crm-documents/ui';
import { formatCurrency } from '@paeffgen/react/crm-documents/util';
import { FavoriteLabelPopover } from './favorite-label-popover';

interface FavoriteCardProps {
  article: FavoriteItem;
  /** Name → persistierte Palette-Farbe (null = aus Name abgeleitet). */
  colorByLabel: Record<string, string | null>;
  selectMode: boolean;
  isSelected: boolean;
  inCart: boolean;
  onToggleSelect: (articleNumber: string) => void;
  onTogglePin: (article: FavoriteItem, e: React.MouseEvent) => void;
  onAddToCart: (article: FavoriteItem) => void;
  /** Kebab → Aktionssheet/-Popover. rect = Anker. */
  onMenu: (article: FavoriteItem, anchor: DOMRect) => void;
}

/**
 * Einheitliche, responsive Favoriten-Karte (ersetzt den Grid/List-Umschalter).
 *
 * Aufbau nach Design (`favoriten.css` `.fav-art-card`):
 *  - Top-Zeile (Grid 40px/1fr/auto): Bild bzw. Auswahl-Checkbox · Name +
 *    Spec + SKU + farbige Label-Pills + Label-Popover · Pin-Toggle.
 *  - Aktionszeile (border-top): Preis links · „Anfrage"/„In Anfrage" + Kebab.
 *  - Angepinnt: orangener linker Rand + getöntes Bild-Badge.
 *  - Select-Mode: ganze Karte toggelt die Auswahl; Pin/Kebab/Popover entfallen.
 */
export function FavoriteCard({
  article,
  colorByLabel,
  selectMode,
  isSelected,
  inCart,
  onToggleSelect,
  onTogglePin,
  onAddToCart,
  onMenu,
}: FavoriteCardProps) {
  const kebabRef = useRef<HTMLButtonElement>(null);

  /**
   * Öffnet das Aktionsmenü – verankert am Kebab, egal ob Bild, Titel oder „…"
   * geklickt wurde. So geht das Desktop-Popover immer an derselben Stelle auf,
   * und der Titel öffnet (statt wie früher in den Shop zu navigieren) das Menü.
   */
  const openMenu = () => {
    const rect = kebabRef.current?.getBoundingClientRect();
    if (rect) onMenu(article, rect);
  };

  return (
    <div
      role={selectMode ? 'button' : undefined}
      tabIndex={selectMode ? 0 : undefined}
      onClick={() => selectMode && onToggleSelect(article.articleNumber)}
      onKeyDown={(e) => {
        if (selectMode && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onToggleSelect(article.articleNumber);
        }
      }}
      className={clsx(
        'group relative flex flex-col gap-2.5 rounded-2xl border bg-white p-3.5 transition-colors',
        article.pinned && !isSelected && 'border-l-[3px] border-l-primary',
        selectMode && 'cursor-pointer active:scale-[0.995]',
        isSelected
          ? 'border-primary ring-1 ring-primary bg-orange-50/40'
          : 'border-gray-200 hover:bg-gray-50',
      )}
    >
      {/* Top row: lead + body + tools */}
      <div className="grid grid-cols-[32px_1fr_auto] gap-3 items-start">
        {/* Lead: select-checkbox or product image (Größe analog Belege) */}
        {selectMode ? (
          <span
            className={clsx(
              'w-8 h-8 inline-flex items-center justify-center rounded-lg border shrink-0 transition-colors',
              isSelected
                ? 'bg-primary border-primary text-white'
                : 'bg-white border-gray-300 text-transparent',
            )}
          >
            {isSelected && <Check size={14} strokeWidth={3} />}
          </span>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openMenu();
            }}
            aria-label={`Aktionen für ${article.description}`}
            aria-haspopup="menu"
            className="relative shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <ProductImage
              src={article.imageUrl}
              alt={article.description}
              size="xs"
            />
            {article.pinned && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-primary text-white inline-flex items-center justify-center border-2 border-white">
                <Pin size={8} fill="currentColor" />
              </span>
            )}
          </button>
        )}

        {/* Body */}
        <div className="min-w-0">
          {/* Titel öffnet das Aktionsmenü (kein Shop-Link mehr — Versehens-Klicks
              navigieren nicht mehr weg, sondern öffnen nur das dismissbare Menü). */}
          <h3
            className="text-[14.5px] font-bold text-secondary leading-snug line-clamp-2 break-words"
            title={article.description}
          >
            {selectMode ? (
              article.description
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openMenu();
                }}
                aria-haspopup="menu"
                className="inline text-left bg-transparent border-0 p-0 m-0 font-inherit text-inherit cursor-pointer transition-colors hover:text-primary focus-visible:outline-none focus-visible:text-primary"
              >
                {article.description}
              </button>
            )}
          </h3>
          {article.description2 && (
            <p
              className="text-xs text-text-muted leading-snug mt-0.5 line-clamp-2"
              title={article.description2}
            >
              {article.description2}
            </p>
          )}
          <p className="text-[10.5px] text-text-faint font-mono tracking-wide mt-1">
            {article.articleNumber}
          </p>

          {/* Label-Pills (read-only) + Label-Popover (nicht im Select-Mode) */}
          {(article.labels.length > 0 || !selectMode) && (
            <div className="mt-2 flex flex-wrap items-center gap-1">
              {article.labels.map((label) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-50 border border-gray-200 text-text-muted text-[10.5px] font-bold uppercase tracking-wide"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{
                      backgroundColor: resolveLabelColor(
                        colorByLabel[label] ?? null,
                        label,
                      ),
                    }}
                  />
                  {label}
                </span>
              ))}
              {!selectMode && (
                <FavoriteLabelPopover
                  articleNumber={article.articleNumber}
                  currentLabels={article.labels}
                />
              )}
            </div>
          )}
        </div>

        {/* Tools: pin (nicht im Select-Mode) */}
        {!selectMode && (
          <div className="flex items-center -mt-0.5 -mr-1 shrink-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onTogglePin(article, e);
              }}
              aria-label={article.pinned ? 'Anpinnen aufheben' : 'Anpinnen'}
              aria-pressed={article.pinned}
              title={
                article.pinned
                  ? 'Von Dashboard entfernen'
                  : 'Auf Dashboard anpinnen'
              }
              className={clsx(
                'w-8 h-8 inline-flex items-center justify-center rounded-lg transition-colors',
                article.pinned
                  ? 'text-primary hover:bg-orange-50'
                  : 'text-text-faint hover:text-gray-500 hover:bg-gray-50',
              )}
            >
              <Pin size={17} fill={article.pinned ? 'currentColor' : 'none'} />
            </button>
          </div>
        )}
      </div>

      {/* Action row: price + add + kebab (nicht im Select-Mode) */}
      {!selectMode && (
        <div
          className="mt-auto flex items-center justify-between gap-3 pt-2.5 border-t border-gray-100"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Preis aus der letzten Rechnung (VRG) — identisch zur Artikelsuche.
              Ohne Rechnung wird bewusst kein Preis angezeigt. */}
          <div className="min-w-0 flex flex-col gap-px">
            {article.lastNetUnitPrice != null && (
              <>
                <span className="text-base font-extrabold text-secondary tabular-nums leading-none">
                  {formatCurrency(article.lastNetUnitPrice)}
                </span>
                <span className="text-[10.5px] text-text-muted font-medium">
                  pro {article.unit || 'Stk'}
                </span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => onAddToCart(article)}
              className={clsx(
                'h-9 px-3.5 rounded-lg shadow-sm transition-colors inline-flex items-center justify-center gap-1.5 text-sm font-bold',
                inCart
                  ? 'bg-orange-50 text-primary border border-primary hover:bg-orange-100'
                  : 'bg-primary hover:bg-primary-hover text-white',
              )}
              title={inCart ? 'Aus Anfrage entfernen' : 'Zur Anfrage'}
            >
              {inCart ? (
                <Check size={16} strokeWidth={2.5} />
              ) : (
                <ClipboardList size={16} strokeWidth={2.5} />
              )}
              <span>{inCart ? 'In Anfrage' : 'Anfrage'}</span>
            </button>
            <button
              ref={kebabRef}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openMenu();
              }}
              aria-label="Aktionen"
              aria-haspopup="menu"
              className="w-[38px] h-[38px] inline-flex items-center justify-center rounded-lg bg-gray-50 border border-gray-200 text-text-muted hover:bg-gray-100 transition-colors shrink-0"
            >
              <MoreVertical size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
